
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm min-h-[44px] active:scale-[0.97] hover:-translate-y-[1px] active:translate-y-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 border-primary/20",
        /** Headline action — gradient + brand shadow. Use once per view. */
        hero:
          "text-primary-foreground border-transparent [background-image:var(--gradient-primary)] [box-shadow:var(--shadow-primary)] hover:brightness-105",
        /** Quiet primary — tinted fill, no heavy weight. */
        soft:
          "bg-primary/10 text-primary border-primary/15 hover:bg-primary/15",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-destructive/20",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border-secondary/20",
        ghost: "border-transparent shadow-none hover:bg-accent hover:text-accent-foreground text-foreground",
        link: "text-primary underline-offset-4 hover:underline border-transparent shadow-none hover:translate-y-0",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 min-h-[36px] rounded-lg px-3 text-[13px]",
        lg: "min-h-[52px] h-[52px] rounded-2xl px-7 text-base",
        icon: "h-11 w-11 min-h-[44px] px-0",
        pill: "h-11 rounded-full px-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)


export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
